import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/store';
import { useThemeStore } from '../../../../store/ConfigStore';
import html2canvas from 'html2canvas'; // 引入html2canvas用于截图
// import { InvationV13SummaryResponse } from '../../../api/invitationV13';
import { InvationV13SummaryResponse } from '@/types/invitation';
import { useToast } from '../../../../components/Toast';
// 导入分享平台图标
import SvgIcon from '../../../../components/SvgIcon';
import CopyIcon from '../../../../assets/svg/copy.svg';
import CopyDarkIcon from '../../../../assets/svg/copyDark.svg';
import CheckIcon from '../../../../assets/svg/check-line.svg';
import CheckDarkIcon from '../../../../assets/svg/check-line-dark.svg';
// import WhatsAppIcon from '../../../../assets/svg/Whatsapp.svg';
// import LineIcon from '../../../../assets/svg/Line.svg';
// import TelegramIcon from '../../../../assets/svg/Telegram.svg';
// import TwitterIcon from '../../../../assets/svg/Twitter.svg';
// import FaceBookIcon from '../../../../assets/svg/Facebook.svg';
import WhatsAppIcon from '../../../../assets/svg/Grey_Whatsapp.svg';
import LineIcon from '../../../../assets/svg/Grey_Line.svg';
import TelegramIcon from '../../../../assets/svg/Grey_Telegram.svg';
import TwitterIcon from '../../../../assets/svg/Grey_Twitter.svg';
import FaceBookIcon from '../../../../assets/svg/Grey_Facebook.svg';
import Dialog from '../../../../components/Dialog';

interface InviteFriendProps {
    visible: boolean;
    onClose: () => void;
    inviteCode: string;
    isCopied?: boolean;
    isLoggedIn: boolean;
    summaryData?: InvationV13SummaryResponse | null;
    isBound?: boolean;
}

const InviteFriendDialog: React.FC<InviteFriendProps> = ({
    visible,
    onClose,
    inviteCode,
    // isCopied = false,
    isLoggedIn,
    summaryData,
    isBound
}) => {
    const { t } = useTranslation();
    const { UserStore } = useStore();
    const { isDark } = useThemeStore(); // 引入主题状态
    const toast = useToast();

    // 组件内部使用自己的状态
    const [internalIsMobile, setInternalIsMobile] = useState(false);
    const [internalShareOptionsPage, setInternalShareOptionsPage] = useState(0);
    const [internalTouchStartX, setInternalTouchStartX] = useState(0);
    const [internalIsCopied, setInternalIsCopied] = useState(false);

    // 邀请卡引用，用于截图下载
    const inviteCardRef = React.useRef<HTMLDivElement>(null);

    // 小屏幕下每页显示的分享选项数量
    const itemsPerPage = 4;

    // 获取邀请比例
    const getReferralRate = (): string => {
        return summaryData?.referral_rate || '50%'; // 默认值为50%
    };

    // 获取邀请码
    const getReferralCode = (): string => {
        return summaryData?.referral_code || UserStore?.userInviteCode || '--------';
    };

    // 分享选项
    const shareOptions = useMemo(() => [
        { icon: WhatsAppIcon, label: 'whatsapp' },
        { icon: LineIcon, label: 'line' },
        { icon: TelegramIcon, label: 'telegram' },
        { icon: TwitterIcon, label: 'twitter' },
        { icon: FaceBookIcon, label: 'facebook' }
    ], []);

    // 在组件挂载时检测一次设备类型并固定
    useEffect(() => {
        const handleResize = () => {
            setInternalIsMobile(window.innerWidth < 768);
        };
        handleResize(); // 初始化时也执行一次
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // 使用useCallback避免函数重建
    const handleInternalTouchStart = useCallback((e: React.TouchEvent) => {
        setInternalTouchStartX(e.touches[0].clientX);
    }, []);

    const handleInternalTouchEnd = useCallback((e: React.TouchEvent) => {
        const touchEndX = e.changedTouches[0].clientX;
        const diff = internalTouchStartX - touchEndX;

        if (Math.abs(diff) > 30) {
            if (diff > 0) {
                setInternalShareOptionsPage(prev => (prev + 1) % Math.ceil(shareOptions.length / itemsPerPage));
            } else {
                setInternalShareOptionsPage(prev => (prev === 0 ? Math.ceil(shareOptions.length / itemsPerPage) - 1 : prev - 1));
            }
        }
    }, [internalTouchStartX, shareOptions.length]);

    // 使用useMemo缓存计算结果
    const currentPageOptions = useMemo(() => {
        return internalIsMobile
            ? shareOptions.slice(internalShareOptionsPage * itemsPerPage, (internalShareOptionsPage + 1) * itemsPerPage)
            : shareOptions;
    }, [internalIsMobile, internalShareOptionsPage, shareOptions]);

    // QRCode参数也用useMemo缓存
    const qrCodeValue = useMemo(() =>
        `https://app.qubit.trade/join/${getReferralCode()}`,
        [UserStore?.userInviteCode, summaryData]);

    // 根据主题动态生成CSS样式
    const getScrollbarStyles = useMemo(() => {
        return `
            /* 全局重置滚动条样式 */
            ::-webkit-scrollbar {
                width: 4px;
                height: 4px;
                background-color: transparent !important;
            }
            
            ::-webkit-scrollbar-track {
                background-color: transparent !important;
                border: none;
                box-shadow: none !important;
            }
            
            ::-webkit-scrollbar-thumb {
                background-color: ${isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'};
                border-radius: 4px;
            }
            
            ::-webkit-scrollbar-thumb:hover {
                background-color: ${isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'};
            }
            
            ::-webkit-scrollbar-corner {
                background-color: transparent !important;
            }
            
            /* Firefox 的滚动条样式 */
            * {
                scrollbar-width: thin;
                scrollbar-color: ${isDark ? 'rgba(255, 255, 255, 0.2) transparent' : 'rgba(0, 0, 0, 0.2) transparent'};
            }
            
            /* 特定容器样式 */
            .scroll-container {
                display: flex;
                width: max-content;
                min-width: 100%;
                justify-content: space-between;
                padding: 0 6px;
                background-color: transparent;
                overflow-x: auto;
                -ms-overflow-style: none; /* IE and Edge */
                white-space: nowrap;
                flex-wrap: nowrap;
            }
            
            .icon-container {
                width: 88px;
                display: inline-flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                gap: 8px;
                flex-shrink: 0;
                cursor: pointer;
                background-color: transparent;
            }
            
            .icon-img {
                width: 24px;
                height: 24px;
            }
            
            .icon-text {
                width: 100%;
                text-align: center;
                color: ${isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'};
                font-size: 12px;
                line-height: 16px;
                font-family: 'PingFang SC';
            }
        `;
    }, [isDark]);

    // 下载邀请卡图片
    const handleDownload = async () => {
        if (inviteCardRef.current) {
            try {
                const canvas = await html2canvas(inviteCardRef.current, {
                    useCORS: true,
                    backgroundColor: null, // 保持透明背景
                    scale: 2, // 提高图片清晰度
                    logging: false
                });

                // 转换为图片
                const image = canvas.toDataURL('image/png');

                // 创建下载链接
                const link = document.createElement('a');
                link.href = image;
                link.download = 'QuBit-Invite-Card.png';
                link.click();
            } catch (error) {
                console.error('下载邀请卡失败:', error);
            }
        }
    };

    // 准备分享内容
    const prepareShareContent = async () => {
        let imageUrl = '';
        const inviteLink = `https://app.qubit.trade/join/${getReferralCode()}`;
        const text = `${t('invitationPromotion.invitationLink.share.shareMessage')} ${inviteLink}`;

        // 将卡片转为图片URL
        if (inviteCardRef.current) {
            try {
                const canvas = await html2canvas(inviteCardRef.current, {
                    useCORS: true,
                    scale: 2,
                    backgroundColor: null,
                    logging: false
                });
                imageUrl = canvas.toDataURL('image/png');
            } catch (error) {
                console.error('生成分享图片失败:', error);
            }
        }

        return { imageUrl, text, url: inviteLink };
    };

    // 创建临时Toast提示
    const showTempToast = (type: 'success' | 'error', title: string, message: string) => {
        const tempElement = document.createElement('div');
        tempElement.style.position = 'fixed';
        tempElement.style.top = '20px';
        tempElement.style.right = '20px';
        tempElement.style.zIndex = '999999';
        tempElement.style.backgroundColor = isDark ? '#1E1E1E' : '#FFFFFF';
        tempElement.style.color = isDark ? '#FFFFFF' : '#000000';
        tempElement.style.padding = '16px';
        tempElement.style.borderRadius = '8px';
        tempElement.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
        tempElement.style.opacity = '0';
        tempElement.style.transform = 'translateX(100%)';
        tempElement.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
        tempElement.style.minWidth = '320px';
        tempElement.style.maxWidth = '400px';
        tempElement.style.display = 'flex';
        tempElement.style.alignItems = 'flex-start';

        // 创建图标
        const iconDiv = document.createElement('div');
        iconDiv.style.marginRight = '12px';
        iconDiv.style.flexShrink = '0';
        iconDiv.style.width = '24px';
        iconDiv.style.height = '24px';
        iconDiv.style.display = 'flex';
        iconDiv.style.alignItems = 'center';
        iconDiv.style.justifyContent = 'center';

        if (type === 'success') {
            iconDiv.style.color = '#4CAF50'; // 成功绿色
            iconDiv.innerHTML = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" fill="currentColor" fill-opacity="0.2"/>
                <path d="M7.75 12.75L10.25 15.25L16.25 8.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>`;
        } else {
            iconDiv.style.color = '#F44336'; // 错误红色
            iconDiv.innerHTML = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" fill="currentColor" fill-opacity="0.2"/>
                <path d="M15 9L9 15M9 9L15 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>`;
        }

        // 创建内容区域
        const contentDiv = document.createElement('div');
        contentDiv.style.flexGrow = '1';

        // 创建标题
        const titleP = document.createElement('p');
        titleP.style.margin = '0 0 4px 0';
        titleP.style.fontWeight = '500';
        titleP.style.fontSize = '14px';
        titleP.textContent = title;

        // 创建消息
        const messageP = document.createElement('p');
        messageP.style.margin = '0';
        messageP.style.fontSize = '14px';
        messageP.style.color = isDark ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.6)';
        messageP.textContent = message;

        // 组装内容
        contentDiv.appendChild(titleP);
        contentDiv.appendChild(messageP);

        // 添加关闭按钮
        const closeBtn = document.createElement('button');
        closeBtn.style.background = 'none';
        closeBtn.style.border = 'none';
        closeBtn.style.cursor = 'pointer';
        closeBtn.style.marginLeft = '12px';
        closeBtn.style.padding = '0';
        closeBtn.style.color = isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.3)';
        closeBtn.innerHTML = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>`;
        closeBtn.onclick = () => {
            tempElement.style.opacity = '0';
            tempElement.style.transform = 'translateX(100%)';
            setTimeout(() => {
                if (document.body.contains(tempElement)) {
                    document.body.removeChild(tempElement);
                }
            }, 300);
        };

        // 组装主元素
        tempElement.appendChild(iconDiv);
        tempElement.appendChild(contentDiv);
        tempElement.appendChild(closeBtn);

        document.body.appendChild(tempElement);

        // 强制回流后显示元素（添加动画效果）
        setTimeout(() => {
            tempElement.style.opacity = '1';
            tempElement.style.transform = 'translateX(0)';
        }, 10);

        // 3秒后自动移除
        setTimeout(() => {
            tempElement.style.opacity = '0';
            tempElement.style.transform = 'translateX(100%)';
            setTimeout(() => {
                if (document.body.contains(tempElement)) {
                    document.body.removeChild(tempElement);
                }
            }, 300);
        }, 3000);

        return tempElement;
    };

    // 复制邀请链接功能
    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(qrCodeValue);
            console.log('复制链接成功，准备显示Toast');
            setInternalIsCopied(true);
            setTimeout(() => {
                setInternalIsCopied(false);
            }, 2000);
            // 使用toast提示复制成功
            const toastId = toast.success({
                message: t('invitationPromotion.invitationLink.share.copySuccessMsg') || '链接已复制到剪贴板'
            });

            console.log('Toast显示调用完成，Toast ID:', toastId);

        } catch (err) {
            console.error('复制链接失败:', err);
            // 使用toast提示复制失败
            toast.error({
                message: t('invitationPromotion.invitationLink.share.copyFailedMsg') || '复制链接失败，请手动复制'
            });
        }
    };

    // 社交平台分享
    const handleShare = async (platform: string) => {
        // 处理复制链接
        if (platform === 'copyLink') {
            handleCopyLink();
            return;
        }

        const { imageUrl, text, url } = await prepareShareContent();
        const encodedText = encodeURIComponent(text);
        const encodedUrl = encodeURIComponent(url);

        // 根据不同平台，打开不同的分享链接
        let shareUrl = '';

        switch (platform) {
            case 'whatsapp':
                // WhatsApp不直接支持图片URL分享，只能分享文本和链接
                shareUrl = `https://api.whatsapp.com/send/?text=${encodedText}`;
                break;

            case 'line':
                // LINE可以分享文本和URL
                shareUrl = `https://social-plugins.line.me/lineit/share?url=${encodedUrl}&text=${encodedText}`;
                break;

            case 'telegram':
                // Telegram可以分享URL和文本
                shareUrl = `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`;
                break;

            case 'twitter':
                // Twitter (X) 可以分享文本和URL
                shareUrl = `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`;
                break;

            case 'facebook':
                // Facebook可以分享URL
                shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
                break;

            default:
                break;
        }

        // 如果是移动设备，尝试使用Web Share API (如果浏览器支持)
        if (navigator.share && internalIsMobile) {
            try {
                // 创建一个Blob对象
                const response = await fetch(imageUrl);
                const blob = await response.blob();
                const file = new File([blob], 'invite-card.png', { type: 'image/png' });

                await navigator.share({
                    title: t('invitationPromotion.invitationLink.share.shareTitle') || 'QuBit Invitation',
                    text: text.replace(url, ''), // 避免文本中出现URL
                    url: url,
                    files: [file]
                });
                return;
            } catch (error) {
                console.error('Web Share API 分享失败:', error);
                // 如果Web Share API失败，回退到打开链接
            }
        }

        // 打开分享链接
        if (shareUrl) {
            window.open(shareUrl, '_blank');
        }
    };

    // 拖动滑动相关ref和事件
    const scrollRef = React.useRef<HTMLDivElement>(null);
    const isDragging = React.useRef(false);
    const startX = React.useRef(0);
    const scrollLeft = React.useRef(0);

    const handleMouseDown = (e: React.MouseEvent) => {
        isDragging.current = true;
        startX.current = e.pageX - (scrollRef.current?.offsetLeft || 0);
        scrollLeft.current = scrollRef.current?.scrollLeft || 0;
    };
    const handleMouseLeave = () => {
        isDragging.current = false;
    };
    const handleMouseUp = () => {
        isDragging.current = false;
    };
    const handleMouseMove = (e: React.MouseEvent) => {
        if (!isDragging.current) return;
        e.preventDefault();
        const x = e.pageX - (scrollRef.current?.offsetLeft || 0);
        const walk = x - startX.current;
        if (scrollRef.current) {
            scrollRef.current.scrollLeft = scrollLeft.current - walk;
        }
    };
    // 移动端触摸事件
    const handleTouchStart = (e: React.TouchEvent) => {
        isDragging.current = true;
        startX.current = e.touches[0].pageX - (scrollRef.current?.offsetLeft || 0);
        scrollLeft.current = scrollRef.current?.scrollLeft || 0;
    };
    const handleTouchEnd = () => {
        isDragging.current = false;
    };
    const handleTouchMove = (e: React.TouchEvent) => {
        if (!isDragging.current) return;
        const x = e.touches[0].pageX - (scrollRef.current?.offsetLeft || 0);
        const walk = x - startX.current;
        if (scrollRef.current) {
            scrollRef.current.scrollLeft = scrollLeft.current - walk;
        }
    };

    return (
        <Dialog
            visible={visible}
            onClose={onClose}
            title={<></>}
            footer={null}
            width="500px"
            center={false}
            mobileWidth="90%"
            closeOnClickModal={true}
            showClose={true}
            backdropBlur={3}
            customClass="p-6"
            bodyStyle={{ padding: 0 }}
            headerStyle={{ padding: 0 }}
            footerStyle={{ padding: 0 }}
        >
            <div className="flex flex-col w-full">
                <div className="w-full flex flex-col items-center justify-center text-center mt-[14px] mb-6">
                    <div className="text-xl font-semibold font-['Inter'] text-sel-normal dark:text-dark-sel-normal leading-tight mb-6">{t('invitationPromotion.invitationLink.share.title')}</div>
                    {/* <div className="text-sm font-semibold font-['Inter'] text-sel-normal dark:text-dark-sel-normal leading-tight mb-6">{t('invitationPromotion.invitationLink.share.inviteFriendsDes', { referralRate: getReferralRate(), discount: 5 })}</div> */}
                    <div className="text-xs font-semibold font-['Inter'] text-sel-normal dark:text-dark-sel-normal leading-tight mb-2">{t('invitationPromotion.invitationLink.share.yourCode')} {getReferralCode()}</div>
                    <div className="flex items-center gap-1 bg-button dark:bg-dark-button rounded-lg text-xs font-medium font-['Inter'] text-sel-normal dark:text-dark-sel-normal leading-tight px-4 py-3">
                        <div>{qrCodeValue}</div>
                        <button
                            onClick={() => handleCopyLink()}
                            className="hover:bg-hover dark:hover:bg-dark-hover rounded-full transition-colors"
                        >
                            <img src={internalIsCopied ? (isDark ? CheckDarkIcon : CheckIcon) : (isDark ? CopyDarkIcon : CopyIcon)} alt="Code" className="w-3 h-3" />
                            {/* <SvgIcon 
                                src={
                                    internalIsCopied ? (isDark ? CheckDarkIcon : CheckIcon) : (isDark ? CopyDarkIcon : CopyIcon)
                                }
                                className="w-4 h-4"
                            /> */}
                        </button>
                    </div>
                </div>
                {/* 分享选项区域 - 简化实现 */}
                {internalIsMobile ? (
                    <div className="self-stretch py-6">
                        <div
                            ref={scrollRef}
                            style={{
                                width: '100%',
                                overflowX: 'auto',
                                overflowY: 'hidden', // 禁止垂直滚动
                                userSelect: 'none', // 禁止选中内容
                                WebkitOverflowScrolling: 'touch',
                                background: 'transparent',
                                cursor: isDragging.current ? 'grabbing' : 'grab',
                                scrollbarWidth: 'none'
                            }}
                            className="scroll-container custom-scrollbar"
                            onMouseDown={handleMouseDown}
                            onMouseLeave={handleMouseLeave}
                            onMouseUp={handleMouseUp}
                            onMouseMove={handleMouseMove}
                            onTouchStart={handleTouchStart}
                            onTouchEnd={handleTouchEnd}
                            onTouchMove={handleTouchMove}
                        >
                            <div className="flex gap-6 min-w-full px-1.5" style={{ width: 'max-content' }}>
                                {shareOptions.map((option, index) => (
                                    <div
                                        key={index}
                                        className="w-[88px] flex flex-col items-center gap-2 flex-shrink-0 cursor-pointer"
                                        onClick={() => {handleShare(option.label)}}
                                    >
                                        <img src={option.icon} alt={t(`invitationPromotion.invitationLink.share.${option.label}`)} className="w-6 h-6" />
                                        <div className={`text-center ${isDark ? 'text-dark-normal' : 'text-normal'} text-xs font-normal font-['PingFang_SC'] leading-none`}>
                                            {t(`invitationPromotion.invitationLink.share.${option.label}`)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <style>
                            {getScrollbarStyles}
                        </style>
                    </div>
                ) : (
                    <div className="self-stretch px-1.5 py-6">
                        <div className="inline-flex justify-between items-center w-full">
                            {shareOptions.map((option, index) => (
                                <div
                                    key={index}
                                    className="flex-1 inline-flex flex-col justify-start items-center gap-2 cursor-pointer"
                                    onClick={() => {handleShare(option.label)}}
                                >
                                    <img src={option.icon} alt={t(`invitationPromotion.invitationLink.share.${option.label}`)} className="w-6 h-6" />
                                    <div className={`self-stretch text-center ${isDark ? 'text-dark-normal' : 'text-normal'} text-xs font-normal font-['PingFang_SC'] leading-none`}>
                                        {t(`invitationPromotion.invitationLink.share.${option.label}`)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </Dialog>
    );
};

export default InviteFriendDialog;